import React, { useState, useEffect } from 'react';
import './GlobalUnitsManager.css';
import { 
  getGlobalUnitsConfig, 
  updateGlobalUnitSettings,
  clearGlobalUnitsCache 
} from '../utils/globalUnits';

const GlobalUnitsManager = ({ onClose }) => {
  const [config, setConfig] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const configData = await getGlobalUnitsConfig();
      if (configData) {
        setConfig(configData);
        setSettings({ ...configData.globalSettings });
      } else {
        setError('Failed to load global units configuration');
      }
    } catch (err) {
      console.error('Error loading global units config:', err);
      setError('Failed to load configuration: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnitChange = (category, unit) => {
    setSettings(prev => ({
      ...prev,
      [category]: unit
    }));
    setSuccessMessage(''); // Clear success message when changes are made
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const result = await updateGlobalUnitSettings(settings);
      setSuccessMessage(`Global units updated successfully! Backup created: ${result.backup}`);
      
      // Reload config to get latest data
      await loadConfig();
      
    } catch (err) {
      console.error('Error updating global units:', err);
      setError('Failed to update settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (config) {
      setSettings({ ...config.globalSettings });
      setSuccessMessage('');
      setError(null);
    }
  };

  const handleRefresh = async () => {
    clearGlobalUnitsCache();
    await loadConfig();
    setSuccessMessage('Configuration refreshed from server');
  };

  if (loading) {
    return (
      <div className="global-units-overlay">
        <div className="global-units-manager">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading global units configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="global-units-overlay">
        <div className="global-units-manager">
          <div className="error-container">
            <h3>Error</h3>
            <p>{error || 'Failed to load configuration'}</p>
            <div className="error-actions">
              <button onClick={loadConfig} className="btn btn-primary">Retry</button>
              <button onClick={onClose} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(config.globalSettings);

  return (
    <div className="global-units-overlay">
      <div className="global-units-manager">
        <div className="manager-header">
          <h2>Global Units Configuration</h2>
          <div className="header-actions">
            <button 
              onClick={handleRefresh} 
              className="btn btn-secondary"
              title="Refresh from server"
            >
              🔄 Refresh
            </button>
            <button onClick={onClose} className="btn-close">×</button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span>❌ {error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            <span>✅ {successMessage}</span>
            <button onClick={() => setSuccessMessage('')}>×</button>
          </div>
        )}

        <div className="manager-content">
          <div className="units-grid">
            {Object.entries(config.unitCategories).map(([categoryKey, category]) => (
              <div key={categoryKey} className="unit-category">
                <div className="category-header">
                  <h3>{category.name}</h3>
                  <p className="category-description">{category.description}</p>
                </div>
                
                <div className="unit-selector">
                  <label>Current Global Unit:</label>
                  <select
                    value={settings[categoryKey] || category.defaultUnit}
                    onChange={(e) => handleUnitChange(categoryKey, e.target.value)}
                    className="unit-select"
                  >
                    {category.availableUnits.map(unit => (
                      <option key={unit.value} value={unit.value}>
                        {unit.label}
                      </option>
                    ))}
                  </select>
                  
                  <div className="current-setting">
                    <strong>Selected:</strong> {settings[categoryKey] || category.defaultUnit}
                    {settings[categoryKey] !== config.globalSettings[categoryKey] && (
                      <span className="changed-indicator"> (changed)</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="manager-footer">
          <div className="footer-info">
            <p>
              <strong>Last Modified:</strong> {new Date(config.lastModified).toLocaleString()}
            </p>
            <p>
              <strong>Version:</strong> {config.version}
            </p>
          </div>
          
          <div className="footer-actions">
            <button 
              onClick={handleReset} 
              className="btn btn-secondary"
              disabled={!hasChanges}
            >
              Reset Changes
            </button>
            <button 
              onClick={onClose} 
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave} 
              className={`btn ${hasChanges ? 'btn-primary' : 'btn-secondary'}`}
              disabled={saving || !hasChanges}
            >
              {saving ? 'Saving...' : 'Save Global Units'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalUnitsManager; 